import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from "react";
import styles from "./FilterPanel.module.css";

// FilterPanel (Bheli.Web.Feedback) con el mismo HTML y el mismo comportamiento que FilterPanel.razor.js: en escritorio el
// panel toma el ancho de su fila de filtros ([data-filter-anchor]); en el celular es una hoja inferior. Esc, un clic fuera o
// bajar la manija lo cierran y el foco vuelve al disparador.

export type FilterPanelHandle = { open: () => void; close: () => void };

type Props = {
  label: string;
  clearLabel: string;
  applyLabel: string;
  trigger: ReactNode;
  children: ReactNode;
  /** Pie propio mientras el panel muestra otra pantalla (Etiquetas: «← Elegir fechas» con su «Aplicar»); si no, el de siempre. */
  footer?: ReactNode;
  onPreparing?: () => void;
  onApplying: () => Promise<void> | void;
  onClearing: () => Promise<void> | void;
};

export const FilterPanel = forwardRef<FilterPanelHandle, Props>(function FilterPanel(props, ref) {
  const s = styles.scope;
  const root = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const [applying, setApplying] = useState(false);
  const holdRow = useRef<(() => void) | null>(null);

  const close = () => dialog.current?.close();
  const open = (byKeyboard = true) => {
    const panel = dialog.current,
      button = trigger.current;
    if (!panel || !button || panel.open) return;
    button.setAttribute("aria-expanded", "true");
    if (matchMedia("(max-width:767px)").matches) {
      for (const property of ["top", "left", "width", "max-height"]) panel.style.removeProperty(property);
    } else {
      const box = (root.current?.closest(".c1-search-row,[data-filter-anchor]") ?? button).getBoundingClientRect();
      const top = Math.min(box.bottom + 8, innerHeight - 200);
      Object.assign(panel.style, {
        left: Math.max(12, box.left) + "px",
        top: top + "px",
        width: box.width + "px",
        maxHeight: innerHeight - top - 12 + "px",
      });
    }
    // K3 · C07 1.14.5: abrir o cerrar no desliza la fila de píldoras que lo contiene: el foco que el diálogo pone dentro
    // del panel (y el que vuelve al botón) la movía. Con el panel modal abierto nadie más puede deslizarla, así que se
    // sostiene su posición hasta el cierre; al cerrar, el foco vuelve al botón sin desplazar y la pantalla decide (C07).
    holdRow.current?.();
    const row = scrollingRow(button);
    if (row) {
      const left = row.scrollLeft,
        hold = () => {
          if (row.scrollLeft !== left) row.scrollLeft = left;
        };
      row.addEventListener("scroll", hold);
      holdRow.current = () => {
        holdRow.current = null;
        row.removeEventListener("scroll", hold);
      };
    }
    panel.showModal();
    // C01: el anillo de foco solo con teclado. Abierto con el teclado, el foco va al primer control; con el mouse, al
    // panel mismo (sin anillo): el botón no toma el foco al tocarlo (K3) y Chrome mostraba el anillo en el primer control.
    if (matchMedia("(pointer:fine)").matches && matchMedia("(min-width:768px)").matches) {
      if (byKeyboard) panel.querySelector<HTMLElement>("button,input")?.focus({ preventScroll: true });
      else panel.focus({ preventScroll: true });
    }
  };
  useImperativeHandle(ref, () => ({ open, close }));

  useEffect(() => {
    const panel = dialog.current!,
      button = trigger.current!;
    const abort = new AbortController(),
      options = { signal: abort.signal };
    panel.addEventListener(
      "close",
      () => {
        button.setAttribute("aria-expanded", "false");
        button.focus({ preventScroll: true });
        holdRow.current?.();
      },
      options,
    );
    document.addEventListener(
      "pointerdown",
      (event) => {
        if (!panel.open) return;
        const target = event.target as Element;
        if (target === panel) {
          const box = panel.getBoundingClientRect();
          if (
            event.clientX < box.left ||
            event.clientX > box.right ||
            event.clientY < box.top ||
            event.clientY > box.bottom
          )
            panel.close();
        } else if (!panel.contains(target) && target !== button && !target.closest?.("[popover]:popover-open"))
          panel.close();
      },
      options,
    );
    document.addEventListener(
      "keydown",
      (event) => {
        if (event.key === "Escape" && panel.open && !panel.querySelector("[popover]:popover-open")) {
          event.preventDefault();
          panel.close();
        }
      },
      options,
    );
    let start: number | undefined;
    panel.querySelector(".bh-filter-handle")!.addEventListener(
      "pointerdown",
      (event) => {
        start = (event as PointerEvent).clientY;
      },
      options,
    );
    panel.addEventListener(
      "pointerup",
      (event) => {
        if (start !== undefined && event.clientY - start > 60) panel.close();
        start = undefined;
      },
      options,
    );
    return () => abort.abort();
  }, []);

  const apply = async () => {
    if (applying) return;
    setApplying(true);
    try {
      await props.onApplying();
    } finally {
      setApplying(false);
    }
  };
  return (
    <div className={`bh-filter-host ${s}`} ref={root}>
      <button
        ref={trigger}
        type="button"
        className={`bh-filter-trigger ${s}`}
        aria-haspopup="dialog"
        aria-label={props.label}
        title={props.label}
        // K3: tocar el botón no lo enfoca (el foco va al panel): enfocarlo deslizaba la fila para mostrarlo entero.
        onMouseDown={(event) => event.preventDefault()}
        onClick={(event) => {
          props.onPreparing?.();
          // detail 0: Enter o Espacio (teclado); 1 o más: clic o toque.
          open(event.detail === 0);
        }}
      >
        {props.trigger}
      </button>
      <dialog ref={dialog} className={`bh-filter-panel ${s}`} aria-label={props.label} tabIndex={-1}>
        <div className={`bh-filter-handle ${s}`} aria-hidden="true"></div>
        <div className={`bh-filter-groups ${s}`}>{props.children}</div>
        {props.footer ? (
          <footer className={s}>{props.footer}</footer>
        ) : (
        <footer className={s}>
          <button
            type="button"
            className={`button secondary ${s}`}
            data-part="btn-secundario"
            onClick={() => {
              close();
              void props.onClearing();
            }}
          >
            {props.clearLabel}
          </button>
          <button
            type="button"
            className={`button ${s}`}
            data-part="btn-principal"
            aria-busy={applying ? "true" : "false"}
            onClick={() => void apply()}
          >
            {props.applyLabel}
          </button>
        </footer>
        )}
      </dialog>
    </div>
  );
});

/** La fila con desplazamiento lateral que contiene al botón (la fila de píldoras del celular), si hay. */
function scrollingRow(element: HTMLElement | null): HTMLElement | null {
  for (let node = element?.parentElement; node && node !== document.body; node = node.parentElement)
    if (/(auto|scroll)/.test(getComputedStyle(node).overflowX)) return node;
  return null;
}
